import { documentThemeSlug } from '@xdocs/schemas';
import type { DocumentThemeSlug } from '@xdocs/schemas';

/**
 * F65 client-side Theme catalog — a presentation-only APPROXIMATION of the
 * 8 fixed document Themes rendered by `render-worker`'s `apply_theme` stage
 * (design/app/29-styling.html `DTHEMES`). This is NOT the source of truth
 * for the actual rendered artifact (that lives server-side in `templates`);
 * it exists purely to drive the `/style/:docId` live-preview panel without
 * a round trip, per the task spec ("theme config -> preview render
 * client-side approximation"). A SEPARATE axis from the app light/dark
 * theme (`users.theme`) and the F66 reader theme (`documentThemes.ts`'s
 * sibling `readerThemes.ts`) — never conflate the three (CODE_GUIDELINES §4).
 */
export interface DocumentThemeVisual {
  /** Card/preview page background (hex — approximation only, never persisted). */
  pageBackground: string;
  bodyTextColor: string;
  accent: string;
  bodyFontKind: 'serif' | 'sans';
  /** Short cover-treatment label shown on the preview + gallery card. */
  coverStyle: string;
  /** `'default'` = the pre-selected Theme for new documents; `'dark'` = the
   * one dark-by-default Theme (Noir). Absent for the rest. */
  tag?: 'default' | 'dark';
  pro: boolean;
}

export const DOCUMENT_THEME_SLUGS: readonly DocumentThemeSlug[] = documentThemeSlug.options;

export const DOCUMENT_THEME_VISUALS: Record<DocumentThemeSlug, DocumentThemeVisual> = {
  manuscript: {
    pageBackground: '#f6efe2',
    bodyTextColor: '#3a3026',
    accent: '#8a6d4b',
    bodyFontKind: 'serif',
    coverStyle: 'Full-bleed scrim',
    tag: 'default',
    pro: false,
  },
  academic: {
    pageBackground: '#ffffff',
    bodyTextColor: '#262626',
    accent: '#34506b',
    bodyFontKind: 'serif',
    coverStyle: 'Minimal rule',
    pro: false,
  },
  modern: {
    pageBackground: '#ffffff',
    bodyTextColor: '#1d1d1f',
    accent: '#d9542f',
    bodyFontKind: 'sans',
    coverStyle: 'Banded color',
    pro: false,
  },
  storybook: {
    pageBackground: '#fff6e6',
    bodyTextColor: '#4a3724',
    accent: '#e0883a',
    bodyFontKind: 'serif',
    coverStyle: 'Full-bleed illustrated',
    pro: true,
  },
  technical: {
    pageBackground: '#f3f5f7',
    bodyTextColor: '#23272e',
    accent: '#2f7d6b',
    bodyFontKind: 'sans',
    coverStyle: 'Two-color',
    pro: false,
  },
  editorial: {
    pageBackground: '#ffffff',
    bodyTextColor: '#1a1a1a',
    accent: '#b5273a',
    bodyFontKind: 'sans',
    coverStyle: 'Photographic',
    pro: true,
  },
  minimal: {
    pageBackground: '#ffffff',
    bodyTextColor: '#222222',
    accent: '#222222',
    bodyFontKind: 'sans',
    coverStyle: 'Typographic, no-image',
    pro: false,
  },
  noir: {
    pageBackground: '#1c1a1d',
    bodyTextColor: '#e8e2d8',
    accent: '#c0563a',
    bodyFontKind: 'serif',
    coverStyle: 'Dramatic',
    tag: 'dark',
    pro: true,
  },
};
